/**
 * InfluxDB/VictoriaMetrics Configuration API
 *
 * Manages per-organization InfluxDB configurations for metrics storage.
 * Supports both InfluxDB and VictoriaMetrics (API compatible).
 */

import { apiClient } from './api'

// Types
export type MetricsServerType = 'influxdb' | 'victoriametrics'

export type InfluxDBConfig = {
  id: string
  organization_id: string
  name: string
  server_type: MetricsServerType
  url: string
  org_name: string
  bucket: string
  timeout: number
  is_active: boolean
  created_at: string
  updated_at: string
  created_by_id: string
}

export type InfluxDBConfigCreate = {
  name: string
  server_type: MetricsServerType
  url: string
  org_name: string
  bucket: string
  token: string
  timeout?: number
}

export type InfluxDBConfigUpdate = {
  name?: string
  server_type?: MetricsServerType
  url?: string
  org_name?: string
  bucket?: string
  token?: string
  timeout?: number
  is_active?: boolean
}

export type InfluxDBConfigListResponse = {
  configs: InfluxDBConfig[]
  total: number
}

export type InfluxDBConnectionTestResponse = {
  success: boolean
  message: string
  url?: string
  org?: string
  buckets_count?: number
}

export type InfluxDBTokenTest = {
  url: string
  token: string
  org_name: string
}

// API Methods
export const influxdbApi = {
  /**
   * Create a new InfluxDB configuration for an organization
   */
  createConfig: (organizationId: string, data: InfluxDBConfigCreate): Promise<InfluxDBConfig> =>
    apiClient.post(`/api/v1/influxdb/organizations/${organizationId}/configs`, data),

  /**
   * Get all InfluxDB configurations for an organization
   */
  getConfigsForOrg: (organizationId: string): Promise<InfluxDBConfigListResponse> =>
    apiClient.get(`/api/v1/influxdb/organizations/${organizationId}/configs`),

  /**
   * Get a specific InfluxDB configuration
   */
  getConfig: (configId: string): Promise<InfluxDBConfig> =>
    apiClient.get(`/api/v1/influxdb/configs/${configId}`),

  /**
   * Update an InfluxDB configuration
   */
  updateConfig: (configId: string, data: InfluxDBConfigUpdate): Promise<InfluxDBConfig> =>
    apiClient.put(`/api/v1/influxdb/configs/${configId}`, data),

  /**
   * Delete an InfluxDB configuration
   */
  deleteConfig: (configId: string): Promise<void> =>
    apiClient.delete(`/api/v1/influxdb/configs/${configId}`),

  /**
   * Test connection for an existing configuration
   */
  testConnection: (configId: string): Promise<InfluxDBConnectionTestResponse> =>
    apiClient.post(`/api/v1/influxdb/configs/${configId}/test`),

  /**
   * Test token without saving (for validation before create)
   */
  testToken: (data: InfluxDBTokenTest): Promise<InfluxDBConnectionTestResponse> =>
    apiClient.post('/api/v1/influxdb/test-token', data),
}

export default influxdbApi
